import { updateCurrentClient } from '../AdviserArea/AdviserAreaActions';
import fetchWithDefaults from '../CommonUtils/fetchWithDefaults';
import { httpBodyOptions } from '../CommonUtils/httpBodyOptions';
import { urlWithParams } from '../CommonUtils/urlWithParams';
import { hideAll, showNegativeCashflow } from '../FinancialPlanWarning/FinancialPlanWarningActions';
import { invalidateCache } from '../MonteCarloChart/MonteCarloChartActions';
import { notifyPdfLoadedSection } from '../PDFRenderer/PDFRendererActions';
import { PdfSectionType } from '../PDFRenderer/PDFRendererTypes';
import { formatData, getGoalIndex } from './FinancialPlanHelpers';
import { currentClientId, data as dataSelector } from './FinancialPlanSelector';
import { MESSAGES } from '../CommonConstants';

const action = name => `ADV.FINANCIAL_PLAN.${name}`;

export const REQUEST_DATA = action('REQUEST_DATA');
export const RESET_DATA = action('RESET_DATA');
export const RECEIVE_DATA = action('RECEIVE_DATA');
export const SET_ERRORS = action('SET_ERRORS');
export const SET_ERROR_TYPE = action('SET_ERROR_TYPE');
export const SET_IS_CALIBRATING = action('SET_IS_CALIBRATING');
export const SET_REACT_CALIBRATION_WIDGET = action('SET_REACT_CALIBRATION_WIDGET');
export const UPDATE_GOAL_VIEW_STATE = action('UPDATE_GOAL_VIEW_STATE');
export const SET_GOAL_VIEW_STATE = action('SET_GOAL_VIEW_STATE');
export const UPDATE_GOAL_DATA = action('UPDATE_GOAL_DATA');
export const SET_PLAN_IMPACT_DATA = action('SET_PLAN_IMPACT_DATA');
export const CLEAR_DATA = action('CLEAR_DATA');
export const START_RESET_FINANCIAL_PLAN = action('START_RESET_FINANCIAL_PLAN');
export const FINISH_RESET_FINANCIAL_PLAN = action('FINISH_RESET_FINANCIAL_PLAN');

export const requestData = (clientId, sequenceNumber) => ({
  type: REQUEST_DATA,
  clientId,
  sequenceNumber,
});

export const resetData = clientId => ({
  type: RESET_DATA,
  clientId,
});

export const receiveData = (data, clientId, doNotUpdateCalibration) => ({
  type: RECEIVE_DATA,
  clientId,
  doNotUpdateCalibration,
  data,
});

export const setPlanImpactData = (clientId, planImpactData) => ({
  type: SET_PLAN_IMPACT_DATA,
  clientId,
  planImpactData,
});

export const setErrors = (errors, clientId) => ({
  type: SET_ERRORS,
  errors,
  clientId,
});

export const setErrorType = (errorType, clientId) => ({
  type: SET_ERROR_TYPE,
  errorType,
  clientId,
});

export const setReactCalibrationWidget = (calibration, clientId) => ({
  type: SET_REACT_CALIBRATION_WIDGET,
  calibration,
  clientId,
});

export const updateGoalViewState = (goalId, clientId, viewState) => ({
  type: UPDATE_GOAL_VIEW_STATE,
  goalId,
  clientId,
  viewState,
});

export const setGoalViewState = (goalId, clientId, viewState) => ({
  type: SET_GOAL_VIEW_STATE,
  goalId,
  clientId,
  viewState,
});

export const resetFinancialPlan = (clientID, callback) => async dispatch => {
  dispatch({
    type: START_RESET_FINANCIAL_PLAN,
  });

  try {
    const response = await fetchWithDefaults('/Consumer/FinancialPlanWhatIfs/Reset', {
      showSpinner: true,
      method: 'POST',
      ...httpBodyOptions({
        clientID,
        fpId: ADV.consumer.currentFPID(clientID),
      }),
    });

    const { success } = await response.json();

    if (success) {
      callback();
      dispatch({
        type: FINISH_RESET_FINANCIAL_PLAN,
      });
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err);
  }
};

export const updateGoalData = (goalId, clientId, data) => (dispatch, getState) => {
  const originalData = dataSelector(getState(), {
    clientId,
  });
  const originalGoalIndex = getGoalIndex(originalData, goalId);

  dispatch({
    type: UPDATE_GOAL_DATA,
    clientId,
    originalGoalIndex,
    originalGoal: originalData.Goals[originalGoalIndex],
    data,
  });
};

export const clearFinancialPlanData = clientId => ({
  type: CLEAR_DATA,
  clientId,
});

export const fetchData = (
  fpId,
  clientId,
  showSpinner,
  onDone,
  doNotUpdateCalibration,
  isPrint = false,
) => async (dispatch, getState) => {
  const sequenceNumber = ADV.util.monotonicSequenceGenerator();
  dispatch(requestData(clientId, sequenceNumber));

  try {
    const httpResponse = await fetchWithDefaults(
      urlWithParams('/Consumer/FinancialPlanWhatIfs', {
        clientId,
        fpId,
        IsPdf: isPrint,
      }),
      { showSpinner },
    );
    const { errors, payload, success, errorType } = await httpResponse.json();

    const response = { ...payload.viewData, ...payload.extensions };

    if (success) {
      if (!response.Goals) {
        dispatch(setErrorType('PlanNotStarted', clientId));
        return;
      }
      dispatch(setErrorType(null, clientId));
      dispatch(setErrors([], clientId));

      const isAdviserArea = ADV.util.env('isAdviserArea');

      if (isAdviserArea) {
        dispatch(updateCurrentClient({ IsPlanGenerated: true }));
      }

      if (isAdviserArea || ADV.entities.isOptedIn('ResetPlan')) {
        const isConsumerArea = ADV.util.env('isConsumerArea');

        if (response.HasNegativeCashflow && !isConsumerArea) {
          dispatch(
            showNegativeCashflow(
              'This plan currently has negative cash flow. Click the button to view the cash flow report.',
              clientId,
            ),
          );
        } else {
          dispatch(hideAll(clientId));
        }
      }

      const currentData = dataSelector(getState(), {
        clientId,
      });

      const formattedData = formatData(response);

      if (currentData && currentData.sequenceNumber > sequenceNumber) {
        formattedData.IsCalibrating = currentData.IsCalibrating;
      }

      dispatch(receiveData(formattedData, clientId, doNotUpdateCalibration));

      dispatch(notifyPdfLoadedSection(PdfSectionType.Assumptions));

      if (typeof onDone === 'function') {
        setTimeout(() => onDone(formattedData), 0);
      }
    } else {
      dispatch(setErrorType(errorType, clientId));
      dispatch(setErrors(errors, clientId));
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err);
    dispatch(setErrors([MESSAGES.GENERIC_ERROR], clientId));
  }
};

ADV.NewFinancialPlan = {
  refreshPlan: (hideLoading, doNotUpdateCalibration, whenComplete) => {
    const clientId = currentClientId(ADV.store.getState());
    ADV.store.dispatch(invalidateCache(clientId));

    document.dispatchEvent(
      new CustomEvent('refreshFinancialPlan', {
        detail: {
          showSpinner: !hideLoading,
          onDone: whenComplete,
          doNotUpdateCalibration,
        },
      }),
    );
  },
};

export const setPlanImpact = (clientId, planImpact) => dispatch => {
  dispatch(setPlanImpactData(clientId, formatData(planImpact)));
};
